import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check, Sparkles } from 'lucide-react';
import type { AvailableSalon, AuthResponse } from '../../api/authApi';

interface SalonSelectorProps {
    currentSalonId: string | null;
    availableSalons: AvailableSalon[];
    onSalonChange: (salonId: string) => void;
}

const SalonSelector = ({ currentSalonId, availableSalons, onSalonChange }: SalonSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Get current salon name
    const currentSalon = availableSalons.find(s => s.id === currentSalonId);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSalonChange = async (salonId: string) => {
        if (salonId === currentSalonId) {
            setIsOpen(false);
            return;
        }

        setIsAnimating(true);

        // Update localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user: AuthResponse = JSON.parse(userStr);
                user.salonId = salonId;
                localStorage.setItem('user', JSON.stringify(user));
            } catch (error) {
                console.error('Error updating salon in localStorage:', error);
            }
        }

        // Notify parent
        onSalonChange(salonId);

        // Small delay for animation
        setTimeout(() => {
            setIsOpen(false);
            setIsAnimating(false);
            // Reload to refresh all data with new salon context
            window.location.reload();
        }, 300);
    };

    // Don't render if user has only one salon
    if (availableSalons.length <= 1) {
        return null;
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Salon Selector Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    flex items-center gap-3 px-4 py-2.5 rounded-xl
                    bg-gradient-to-r from-emerald-50 to-teal-50
                    border border-emerald-200/60
                    hover:from-emerald-100 hover:to-teal-100
                    hover:border-emerald-300
                    transition-all duration-300 ease-out
                    group
                    ${isOpen ? 'ring-2 ring-emerald-400/30 shadow-lg shadow-emerald-100' : 'shadow-sm'}
                `}
            >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-inner">
                    <Building2 className="w-4 h-4 text-white" />
                </div>

                <div className="text-left hidden sm:block">
                    <p className="text-xs text-emerald-600/70 font-medium">Salón activo</p>
                    <p className="text-sm font-semibold text-gray-800 truncate max-w-[140px]">
                        {currentSalon?.name || 'Seleccionar'}
                    </p>
                </div>

                <ChevronDown
                    className={`
                        w-4 h-4 text-emerald-500 transition-transform duration-300
                        ${isOpen ? 'rotate-180' : ''}
                        group-hover:text-emerald-600
                    `}
                />

                {/* Multi-salon indicator badge */}
                <div className="absolute top-0 right-0 w-4 h-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm transform translate-x-1 -translate-y-1">
                    <span className="text-[9px] font-bold text-white">{availableSalons.length}</span>
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className={`
                        absolute top-full right-0 mt-2 w-72
                        bg-white rounded-2xl shadow-2xl shadow-emerald-100/50
                        border border-gray-100 overflow-hidden
                        z-50
                        animate-in fade-in slide-in-from-top-2 duration-200
                    `}
                >
                    {/* Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-200" />
                            <p className="text-sm font-medium text-white">Mis Salones</p>
                        </div>
                        <p className="text-xs text-emerald-100 mt-0.5">
                            Tienes acceso a {availableSalons.length} salones
                        </p>
                    </div>

                    {/* Salon List */}
                    <div className="py-2 max-h-64 overflow-y-auto">
                        {availableSalons.map((salon, index) => {
                            const isActive = salon.id === currentSalonId;
                            const isChanging = isAnimating && salon.id !== currentSalonId;

                            return (
                                <button
                                    key={salon.id}
                                    onClick={() => handleSalonChange(salon.id)}
                                    disabled={isAnimating}
                                    className={`
                                        w-full px-4 py-3 flex items-center gap-3
                                        transition-all duration-200
                                        ${isActive
                                            ? 'bg-emerald-50 border-l-4 border-emerald-500'
                                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                                        }
                                        ${isChanging ? 'opacity-50' : ''}
                                        disabled:cursor-wait
                                    `}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    {/* Salon Icon */}
                                    <div className={`
                                        w-10 h-10 rounded-xl flex items-center justify-center
                                        ${isActive
                                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-200'
                                            : 'bg-gray-100'
                                        }
                                        transition-all duration-300
                                    `}>
                                        <Building2 className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                                    </div>

                                    {/* Salon Info */}
                                    <div className="flex-1 text-left">
                                        <p className={`
                                            text-sm font-semibold
                                            ${isActive ? 'text-emerald-700' : 'text-gray-700'}
                                        `}>
                                            {salon.name}
                                        </p>
                                        {isActive && (
                                            <p className="text-xs text-emerald-500 flex items-center gap-1 mt-0.5">
                                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                                Activo ahora
                                            </p>
                                        )}
                                    </div>

                                    {/* Check Icon for Active */}
                                    {isActive && (
                                        <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                                            <Check className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                        <p className="text-xs text-gray-400 text-center">
                            Cambiar de salón recargará la página
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalonSelector;
